import * as React from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { NGLabelText } from "../../../newcomponents/text/labelText";
import { CreateNGAdvancedPortRoute } from "./create_ng_advanced_port_route";
import { NGButton } from "../../../newcomponents/button/Button";
import { CreateNGAdvancedWorkloadSubdomainExactlyOneRouteCheck } from "./create_ng_advanced_workloadSubdomain_exactlyOneRoute_check";
import { FormDryRunAlert } from "./formDryRunAlert";
import NGAlert from "../../../newcomponents/alert";
import { DomainPortRouteEditHeadersModal } from "../routeEditHeadersModal";
import { DomainPortRouteEditMirrorModal } from "../routeEditMirrorModal";
import { DomainInput } from "./types/input";
import { getDefaultDomainPortRouteInput } from "./utils/utils";
import { getDryRunErrorText } from "../../mk8s/getDryRunErrorText";
import { AlertCircle } from "react-feather";

interface Props {
  id: string;
  index: number;
}
export const CreateNGAdvancedPortRoutes: React.FC<Props> = ({ id, index }) => {
  const { control, watch, setValue } = useFormContext<DomainInput>();

  const { append: appendRoute } = useFieldArray({
    control,
    name: `advanced.spec.ports.${index}.routes` as const,
  });

  const [isEditingRouteHeaders, setIsEditingRouteHeaders] = React.useState(false);
  const [isEditingRouteMirrors, setIsEditingRouteMirrors] = React.useState(false);

  const dryRun = watch("dryRun.raw.value");

  const routingMode = watch("advanced.spec.routingMode");

  const gvcName = watch("gvc.name");
  const acceptAll = watch("advanced.spec.acceptAll");
  const routes = watch(`advanced.spec.ports.${index}.routes`);

  function addRoute() {
    appendRoute({
      ...getDefaultDomainPortRouteInput(),
      prefix: `/`,
    });
  }

  // 1 type - all
  // 2 prefix/regex - all
  // 2 replacePrefix - all
  // 1 type (only when either acceptAllHosts or acceptAllSubdomains are enabled)
  // 2 hostPrefix/hostRegex (only when either acceptAllHosts or acceptAllSubdomains are enabled)
  // 2 workloadLink - pathBased
  // 1 port - all
  // 1 replica - workloadSubdomain

  const headersDryRunError = getDryRunErrorText({
    dryRunResponse: dryRun as any,
    path: `spec.ports[${index}]routes`,
    paths: [
      ...routes.flatMap((r, i) =>
        r.headers.map((h) => `spec.ports[${index}].routes[${i}].headers.request.set.${h.name}`),
      ),
    ],
  });

  const mirrorsDryRunError = getDryRunErrorText({
    dryRunResponse: dryRun as any,
    path: `spec.ports[${index}]routes`,
    paths: [
      ...routes.flatMap((r, i) =>
        (r.mirror || []).flatMap((_, mi) => [
          `spec.ports[${index}].routes[${i}].mirror[${mi}]`,
          `spec.ports[${index}].routes[${i}].mirror[${mi}].workloadLink`,
          `spec.ports[${index}].routes[${i}].mirror[${mi}].percent`,
        ]),
      ),
    ],
  });

  const colCount = React.useMemo(() => {
    let val = 0;
    if (routingMode === "pathBased") {
      val = 8;
    }
    if (routingMode === "workloadSubdomain") {
      val = 7;
    }
    if (acceptAll !== "none") {
      val += 3;
    }
    return val;
  }, [routingMode, acceptAll]);

  if (routingMode === "gvcSubdomain") {
    return null;
  }

  if (routingMode === "pathBased" && !gvcName) {
    return <NGAlert className="mb-4 -mt-4" type={"info"} message={`GVC Selection is needed for configuring routes.`} />;
  }

  return (
    <>
      <div key={`port-routes-${id}`} className={`${routes.length < 1 ? "mb-8" : ""}`}>
        <div className="flex items-center gap-2 mb-2">
          <NGLabelText className="cursor-default">Routes</NGLabelText>
          <NGButton variant="primary" size={`small`} onClick={addRoute}>
            Add
          </NGButton>
          <NGButton
            className="ml-2"
            size={"small"}
            variant="secondary"
            onClick={() => setIsEditingRouteHeaders(true)}
            iconPosition={`left`}
            renderIcon={
              headersDryRunError.message
                ? () => <AlertCircle className="h-4" style={{ stroke: `var(--color-danger) !important` }} />
                : undefined
            }
          >
            Edit Headers
          </NGButton>
          <NGButton
            className="ml-2"
            size={"small"}
            variant="secondary"
            onClick={() => setIsEditingRouteMirrors(true)}
            iconPosition={`left`}
            renderIcon={
              mirrorsDryRunError.message
                ? () => <AlertCircle className="h-4" style={{ stroke: `var(--color-danger) !important` }} />
                : undefined
            }
          >
            Edit Mirrors
          </NGButton>
        </div>
        {routes.length > 0 ? (
          <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}>
            <div className="col-span-1">Type</div>
            <div className="col-span-2">Prefix/Regex</div>
            <div className="col-span-2">Replace Prefix</div>
            {acceptAll !== "none" ? (
              <>
                <div className="col-span-1">Type</div>
                <div className="col-span-2">Host Prefix/Host Regex</div>
              </>
            ) : null}
            {routingMode === "pathBased" ? (
              <div className="col-span-2">
                <div className="flex items-center">
                  <span>Workload Link</span>
                  <span className="color-danger">*</span>
                </div>
              </div>
            ) : null}
            <div className="col-span-1">Port</div>
            {routingMode === "pathBased" ? null : routingMode === "workloadSubdomain" ? (
              <div className="col-span-1">Replica</div>
            ) : null}
          </div>
        ) : (
          <div>No Routes</div>
        )}
        {routes.map((r) => (
          <CreateNGAdvancedPortRoute key={r.id} portId={id} portIndex={index} routeId={r.id} />
        ))}
        <CreateNGAdvancedWorkloadSubdomainExactlyOneRouteCheck />
        <FormDryRunAlert path={`spec.ports[${index}]`} />
      </div>
      {isEditingRouteHeaders ? (
        <DomainPortRouteEditHeadersModal
          portId={id}
          portIndex={index}
          onClose={() => {
            setIsEditingRouteHeaders(false);
            const newRoutes = routes.map((r) => ({
              ...r,
              headers: r.headers.filter((h) => !!h.name),
            }));
            setValue(`advanced.spec.ports.${index}.routes`, newRoutes);
          }}
        />
      ) : null}
      {isEditingRouteMirrors ? (
        <DomainPortRouteEditMirrorModal
          portId={id}
          portIndex={index}
          onClose={() => {
            setIsEditingRouteMirrors(false);
            const newRoutes = routes.map((r) => ({
              ...r,
              mirror: r.mirror.filter((m) => !!m.workload.name),
            }));
            setValue(`advanced.spec.ports.${index}.routes`, newRoutes);
          }}
        />
      ) : null}
      <FormDryRunAlert path={`spec.ports[${index}].routes`} />
    </>
  );
};
